import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, tokenStore } from "../lib/api";
import { PageHeader, Pill, Empty } from "../components/Bits";
import { OUTCOME_COLORS, OUTCOME_LABELS, formatDuration, formatRelative, SENTIMENT_COLORS } from "../lib/format";
import { DownloadSimpleIcon, FunnelIcon } from "@phosphor-icons/react";

const OUTCOMES = ["", "appointment_booked", "interested", "whitepaper_sent", "callback_requested", "not_interested", "no_answer", "voicemail"];

export default function Calls() {
    const [calls, setCalls] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/calls", { params: { outcome: filter || undefined, limit: 200 } });
            setCalls(data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const exportCsv = () => {
        const t = tokenStore.get();
        const url = `${API_BASE}/export/calls`;
        fetch(url, { headers: { Authorization: `Bearer ${t}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "calls.csv";
                a.click();
            });
    };

    return (
        <div className="pb-12">
            <PageHeader
                overline="ARCHIVE"
                title="Calls"
                description="Every call placed by your AI agents — searchable, exportable, replayable."
                testid="calls-header"
                actions={
                    <button type="button" onClick={exportCsv} className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="export-calls-button">
                        <DownloadSimpleIcon size={14} weight="bold" /> Export CSV
                    </button>
                }
            />

            <div className="px-8 py-6">
                <div className="swiss-section">
                    <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-3 flex-wrap">
                        <FunnelIcon size={14} weight="bold" className="text-[var(--text-secondary)]" />
                        <span className="overline">FILTER OUTCOME</span>
                        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2.5 py-1.5 border border-[var(--border)] rounded-[2px] text-[12px] bg-white" data-testid="outcome-filter">
                            {OUTCOMES.map((o) => <option key={o} value={o}>{o ? OUTCOME_LABELS[o] : "All outcomes"}</option>)}
                        </select>
                        <span className="ml-auto overline text-[var(--text-secondary)]">{calls.length} CALLS</span>
                    </div>
                    {loading ? (
                        <div className="p-10 text-center text-[var(--text-secondary)] text-[13px]">Loading…</div>
                    ) : calls.length === 0 ? (
                        <Empty title="No calls match" description="Try a different filter, or run a campaign batch." testid="calls-empty" />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[13px]" data-testid="calls-table">
                                <thead>
                                    <tr className="border-b border-[var(--border)] [&>th]:py-3 [&>th]:px-4 [&>th]:overline [&>th]:text-[10px] text-left">
                                        <th>When</th>
                                        <th>Prospect</th>
                                        <th>Company</th>
                                        <th>Outcome</th>
                                        <th>Duration</th>
                                        <th>Sentiment</th>
                                        <th>Next Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {calls.map((c) => (
                                        <tr key={c.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-active)]" data-testid={`call-row-${c.id}`}>
                                            <td className="py-3 px-4 mono text-[var(--text-secondary)]">{formatRelative(c.call_timestamp)}</td>
                                            <td className="py-3 px-4 font-semibold">
                                                <Link to={`/calls/${c.id}`} className="hover:underline" data-testid={`call-link-${c.id}`}>{c.prospect_name}</Link>
                                            </td>
                                            <td className="py-3 px-4">{c.prospect_company || "—"}</td>
                                            <td className="py-3 px-4"><Pill color={OUTCOME_COLORS[c.outcome]}>{OUTCOME_LABELS[c.outcome] || c.outcome}</Pill></td>
                                            <td className="py-3 px-4 mono">{formatDuration(c.duration_sec)}</td>
                                            <td className="py-3 px-4">
                                                <span className="mono text-[12px]" style={{ color: SENTIMENT_COLORS[c.sentiment_label] }}>
                                                    {c.sentiment_label} · {c.sentiment_score}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-[var(--text-secondary)] truncate max-w-[240px]" title={c.next_action || ""}>
                                                {c.next_action || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
